<template>
  <v-dialog v-model="open" max-width="480" persistent>
    <v-card class="wd-card" elevation="0">
      <div class="wd-head">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="#2563eb" class="mr-2">mdi-bank-transfer-out</v-icon>
            <h3 class="wd-title">ถอนเงิน</h3>
          </div>
          <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <p class="wd-sub">ยอดคงเหลือ: <b>฿{{ formatNum(balance) }}</b></p>
      </div>

      <v-divider />

      <div class="wd-body">
        <!-- จำนวนเงิน -->
        <div class="field-label">จำนวนเงินที่ต้องการถอน (บาท)</div>
        <v-text-field
          v-model.number="form.amount"
          type="number" min="100" step="1"
          placeholder="ขั้นต่ำ 100 บาท"
          outlined dense hide-details="auto"
          class="rounded-lg mb-1"
          :rules="[ruleMin]"
        />
        <div class="quick-row mb-4">
          <v-chip
            v-for="n in quickAmounts"
            :key="n"
            class="quick-chip"
            label
            @click="form.amount = n"
          >{{ n.toLocaleString('th-TH') }}</v-chip>
        </div>

        <!-- Bank section header with import button -->
        <div class="d-flex align-center justify-space-between mb-3">
          <span class="section-label">ข้อมูลธนาคาร</span>
          <v-btn
            small
            outlined
            color="#059669"
            class="btn-import"
            :loading="profileLoading"
            @click="importFromProfile"
          >
            <v-icon left x-small>mdi-account-arrow-down</v-icon>
            ดึงจาก Profile
          </v-btn>
        </div>

        <!-- Success banner after import -->
        <div v-if="imported" class="import-banner mb-3">
          <v-icon x-small color="#059669" class="mr-1">mdi-check-circle</v-icon>
          ดึงข้อมูลธนาคารจาก Profile แล้ว — แก้ไขได้ก่อนยืนยัน
        </div>

        <div class="field-label">ธนาคาร</div>
        <v-select
          v-model="form.bankName"
          :items="banks"
          outlined dense hide-details="auto"
          placeholder="เลือกธนาคาร"
          class="rounded-lg mb-4"
        />

        <div class="field-label">เลขบัญชีธนาคาร</div>
        <v-text-field
          v-model="form.bankAccountNumber"
          outlined dense hide-details="auto"
          placeholder="เช่น 123-4-56789-0"
          class="rounded-lg mb-4"
        />

        <div class="field-label">ชื่อบัญชี</div>
        <v-text-field
          v-model="form.bankAccountName"
          outlined dense hide-details="auto"
          placeholder="ชื่อ-นามสกุลตามหน้าบัญชี"
          class="rounded-lg mb-4"
        />

        <div class="field-label">หมายเหตุ (ไม่บังคับ)</div>
        <v-text-field
          v-model="form.note"
          outlined dense hide-details="auto"
          placeholder="หมายเหตุเพิ่มเติม"
          class="rounded-lg"
        />
      </div>

      <v-divider />

      <div class="wd-actions">
        <v-btn class="btn-outline" height="40" @click="close">ยกเลิก</v-btn>
        <v-btn
          class="btn-primary"
          height="40"
          :disabled="!canSubmit"
          :loading="loading"
          @click="submit"
        >
          <v-icon left small>mdi-check</v-icon>
          ยืนยันถอนเงิน
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import WalletService from "@/services/api/wallet";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "WithdrawDialog",
  props: {
    value:   { type: Boolean, default: false },
    balance: { type: Number,  default: 0 },
    role:    { type: String,  default: "user" },
  },
  emits: ["input", "done"],
  data() {
    return {
      loading: false,
      profileLoading: false,
      imported: false,
      form: {
        amount: null,
        bankName: "",
        bankAccountNumber: "",
        bankAccountName: "",
        note: "",
      },
      quickAmounts: [100, 500, 1000, 2000, 5000],
      banks: [
        "กสิกรไทย", "ไทยพาณิชย์", "กรุงเทพ", "กรุงไทย", "กรุงศรีอยุธยา",
        "ทหารไทยธนชาต", "ออมสิน", "ธ.ก.ส.", "ซิตี้แบงก์", "ยูโอบี",
      ],
      ruleMin: (v) => Number(v) >= 100 || "ขั้นต่ำ 100 บาท",
    };
  },
  computed: {
    open: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    canSubmit() {
      return (
        Number(this.form.amount) >= 100 &&
        Number(this.form.amount) <= this.balance &&
        this.form.bankName &&
        this.form.bankAccountNumber.trim() &&
        this.form.bankAccountName.trim()
      );
    },
  },
  methods: {
    close() {
      this.open = false;
      this.resetForm();
    },
    resetForm() {
      this.form = { amount: null, bankName: "", bankAccountNumber: "", bankAccountName: "", note: "" };
      this.imported = false;
    },
    formatNum(n) {
      return Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async importFromProfile() {
      this.profileLoading = true;
      this.imported = false;
      try {
        const info = await WalletService.fetchProfileBank(this.role);
        const hasBankData = info.bankName || info.bankAccountNumber || info.bankAccountName;

        if (!hasBankData) {
          await swalAlert.Error(
            "ไม่พบข้อมูลธนาคาร",
            "กรุณาตั้งค่าบัญชีธนาคารใน Profile ก่อน"
          );
          return;
        }

        if (info.bankName)          this.form.bankName          = info.bankName;
        if (info.bankAccountNumber) this.form.bankAccountNumber = info.bankAccountNumber;
        if (info.bankAccountName)   this.form.bankAccountName   = info.bankAccountName;
        // ถ้า bankAccountName ว่าง ให้ใช้ชื่อเจ้าของ profile แทน
        if (!info.bankAccountName && info.ownerName) {
          this.form.bankAccountName = info.ownerName;
        }

        this.imported = true;
      } catch (e) {
        await swalAlert.Error("ดึงข้อมูลไม่สำเร็จ", e?.response?.data?.message || "เกิดข้อผิดพลาด");
      } finally {
        this.profileLoading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.loading = true;
      try {
        await WalletService.requestWithdrawal({
          amount: Number(this.form.amount),
          bankName: this.form.bankName,
          bankAccountNumber: this.form.bankAccountNumber,
          bankAccountName: this.form.bankAccountName,
          note: this.form.note || "",
        }, this.role);
        await swalAlert.Success("ยื่นคำขอถอนเงินสำเร็จ", "กรุณารอการอนุมัติจาก Admin");
        this.close();
        this.$emit("done");
      } catch (e) {
        await swalAlert.Error("ไม่สำเร็จ", e?.response?.data?.message || "เกิดข้อผิดพลาด");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.wd-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
}

.wd-head {
  padding: 20px 22px 12px;
}

.wd-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.wd-sub {
  color: #6b7280;
  font-size: 13px;
  margin: 6px 0 0;
}

.wd-body {
  padding: 16px 22px 10px;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2a39;
  margin-bottom: 6px;
}

.btn-import {
  text-transform: none !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  letter-spacing: 0 !important;
}

.import-banner {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 12px;
  color: #065f46;
  display: flex;
  align-items: center;
}

.quick-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

::v-deep .quick-chip {
  background: #eff6ff !important;
  border: 1px solid #93c5fd !important;
  font-weight: 700 !important;
  color: #1d4ed8 !important;
  border-radius: 8px !important;
  cursor: pointer;
}

.wd-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 22px 20px;
}

.btn-outline {
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.btn-primary {
  background: #2563eb !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
}
</style>
